import { parse } from 'papaparse'
import { featureCollection, polygon } from '@turf/helpers'
import { FeatureCollection, Polygon, Position } from 'geojson'
import { bngToLatLng } from './latLngToBNG'

// Parses the vertex CSV exported by ArcGIS's Coordinate Tool (e.g.
// gen_atk_gen_coordinatetoolvertices_pnt.csv) into polygon areas.
//
// Expected columns (case-insensitive): Easting, Northing, VertexNo, PartNo and
// optionally the higher-precision x/y pair. Coordinates are British National
// Grid (OSGB36) eastings/northings — we reproject them to WGS84 lng/lat for
// GeoJSON. Each PartNo becomes its own polygon; vertices are ordered by
// VertexNo and the ring is closed automatically.

type ArcGisVertexRow = Record<string, string>

const findKey = (row: ArcGisVertexRow, name: string): string | undefined =>
  Object.keys(row).find(
    (key) => key.trim().toLowerCase() === name.toLowerCase(),
  )

const toNumber = (value: string | undefined): number | null => {
  if (value === undefined) return null
  const parsed = Number(String(value).trim())
  return Number.isFinite(parsed) ? parsed : null
}

export class ArcGisCsvParseError extends Error {}

export const parseArcGisCsv = (csv: string): FeatureCollection<Polygon> => {
  const { data, errors } = parse<ArcGisVertexRow>(csv, {
    header: true,
    skipEmptyLines: true,
  })

  if (errors?.length) {
    throw new ArcGisCsvParseError(errors[0].message)
  }

  const rows = (data ?? []).filter((row) => row && Object.keys(row).length > 0)
  if (!rows.length) {
    throw new ArcGisCsvParseError('The CSV file is empty.')
  }

  const sample = rows[0]
  const eastingKey = findKey(sample, 'Easting')
  const northingKey = findKey(sample, 'Northing')
  // Higher-precision coordinate columns, when present.
  const xKey = findKey(sample, 'x')
  const yKey = findKey(sample, 'y')
  const partKey = findKey(sample, 'PartNo')
  const vertexKey = findKey(sample, 'VertexNo')

  if ((!eastingKey || !northingKey) && (!xKey || !yKey)) {
    throw new ArcGisCsvParseError(
      'Could not find Easting/Northing columns in the CSV file.',
    )
  }

  type ParsedVertex = { part: number; vertexNo: number; position: Position }

  const vertices: ParsedVertex[] = []
  rows.forEach((row, index) => {
    const easting = toNumber(row[xKey]) ?? toNumber(row[eastingKey])
    const northing = toNumber(row[yKey]) ?? toNumber(row[northingKey])
    if (easting === null || northing === null) return

    const { latitude, longitude } = bngToLatLng(easting, northing)
    vertices.push({
      part: toNumber(row[partKey]) ?? 1,
      vertexNo: toNumber(row[vertexKey]) ?? index,
      position: [longitude, latitude],
    })
  })

  if (!vertices.length) {
    throw new ArcGisCsvParseError('No valid coordinates found in the CSV file.')
  }

  // Group vertices by part, then order each part by its vertex number.
  const partsMap = new Map<number, ParsedVertex[]>()
  vertices.forEach((vertex) => {
    const part = partsMap.get(vertex.part) ?? []
    part.push(vertex)
    partsMap.set(vertex.part, part)
  })

  const polygons = Array.from(partsMap.keys())
    .sort((a, b) => a - b)
    .map((part) => {
      const ring = partsMap
        .get(part)
        .sort((a, b) => a.vertexNo - b.vertexNo)
        .map((vertex) => vertex.position)

      // A polygon needs at least 3 distinct vertices.
      if (ring.length < 3) return null

      // Close the ring if the export didn't repeat the first vertex.
      const [firstLng, firstLat] = ring[0]
      const [lastLng, lastLat] = ring[ring.length - 1]
      if (firstLng !== lastLng || firstLat !== lastLat) {
        ring.push([firstLng, firstLat])
      }

      return polygon([ring], { inputType: 'DRAW_ON_MAP' })
    })
    .filter(Boolean)

  if (!polygons.length) {
    throw new ArcGisCsvParseError(
      'The CSV file does not contain a valid area (at least 3 points are required).',
    )
  }

  return featureCollection(polygons) as FeatureCollection<Polygon>
}
